import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {StatusEventPageDataService} from '@services/pages/status-event-page/data/status-event-page-data.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {
  StatusEventPageSignalDataResponse
} from '@models/pages/status-event-page/status-event-page-signal-data-response';
import {
  StatusEventPageEventDisplayDetails
} from '@models/pages/status-event-page/status-event-page-event-display-details';
import {SortEvent, TableHeaderSortableDirective} from '@shared/table-header-sortable/table-header-sortable.directive';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {
  StatusEventPageActionsQueueService
} from '@services/pages/status-event-page/action-events-queue/status-event-page-actions-queue.service';
import {
  StatusEventPageActionsParameters
} from '@models/machine/status-event-signal/status-event-page-actions-parameters';
import {cloneDeep} from 'lodash';
import {StatusEventPage} from '@models/sidebar/response/status-event-page';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {TimeInterval} from '@constants/pages/status-event-page/time-interval';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {SettingsService} from '@services/administration/settings/settings.service';
import {
  StatusEventPagePastDataRequestParameters
} from '@models/pages/status-event-page/status-event-page-past-data-request-parameters';

@Component({
    selector: 'scitis-status-event-page',
    templateUrl: './status-event-page.component.html',
    styleUrls: ['./status-event-page.component.scss'],
    standalone: false
})
export class StatusEventPageComponent implements OnInit, OnDestroy {

  @Input()
  public pageType: MenuItemType = MenuItemType.NONE;
  @Input()
  public pageName: string = '';
  @Input()
  public machineId: string = '';
  @Input()
  public pageId: string = '';
  @Input()
  public organizationId: string | null = null;
  @Input()
  public machineCategory: string | null = null;

  @Input()
  public isTemplatePage: boolean = false;

  @Input()
  public statusEventPageConfiguration: StatusEventPage = new StatusEventPage(-1, '');
  public statusEventsCurrent: StatusEventPageEventDisplayDetails[] = [];
  public statusEventsPast: StatusEventPageEventDisplayDetails[] = [];

  public actionsParameters: StatusEventPageActionsParameters = new StatusEventPageActionsParameters('', [], [], false);
  public refresh_interval: number = StatusEventPageDataService.POLLING_INTERVAL_SECONDS;
  public timeIntervals: any[] = [];
  public showCustomDatePicker: boolean = false;
  public startTimeLabel: string = '';
  public endTimeLabel: string = '';
  public isCurrentReportsTabSelected: boolean = true;
  public lastPageReached: boolean = false;
  public resetPaginationPageIndex = false;

  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();
  public isLoadingPastData: boolean = true;
  public startInterval: Date | null = null;
  public endInterval: Date | null = null;
  public liveDataStreamingEnabled: boolean = true;
  private COMPONENT_NAME: string = 'StatusEventPageComponent';
  private unfilteredEventItemsCurrent: Map<string, StatusEventPageSignalDataResponse[]> = new Map<string, StatusEventPageSignalDataResponse[]>();
  private isPageVisible: boolean = !document.hidden;
  private sortColumn: string = 'timestamp';
  private sortDirection: string = 'desc';
  private customStartDate: number = 0;
  private customEndDate: number = 0;
  public lastUpdateTimestamp: number | null = null;

  private selectedTimeInterval: TimeInterval = TimeInterval.NONE;
  private currentPageIndex: number = 0;
  private itemsPerPageLimit: number = 0;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private statusEventPageDataService: StatusEventPageDataService,
              private statusEventPageActionsQueueService: StatusEventPageActionsQueueService,
              private loadingIndicator: LoadingIndicatorService,
              private tableHeaderSortableService: TableHeaderSortableService,
              private translate: TranslateService,
              private settingsService: SettingsService,
              private notification: NotificationService,
              private changeDetector: ChangeDetectorRef) {
    this.timeIntervals = this.statusEventPageDataService.getIntervalTypes();
    this.startTimeLabel = this.translate.instant('Time Start');
    this.endTimeLabel = this.translate.instant('Time End');
  }

  public get shownInterval(): string {
    if (!this.startInterval || !this.endInterval) {
      return '';
    }

    let interval: string;
    const timezone: string = this.statusEventPageDataService.formatDateTime(this.startInterval.getTime(), '(ZZZZ)');

    switch (this.selectedTimeInterval) {
      case TimeInterval.LAST_HOUR:
      case TimeInterval.CURRENT_DAY:
        interval = `[${this.formatTime(this.startInterval)} - ${this.formatTime(this.endInterval)}] ${timezone}`;
        break;
      case TimeInterval.YESTERDAY:
        interval = `${this.formatDate(this.startInterval)} [${this.formatTime(this.startInterval)} - ${this.formatTime(this.endInterval)}] ${timezone}`;
        break;
      case TimeInterval.LAST_7_DAYS:
        interval = `[${this.formatDateTime(this.startInterval)}  - ${this.formatDateTime(this.endInterval)}]  ${timezone}`;
        break;
      case TimeInterval.CUSTOM:
        interval = `[${this.formatTimestamp(this.customStartDate)} - ${this.formatTimestamp(this.customEndDate)}]  ${timezone}`;
        break;
      case TimeInterval.NONE:
        interval = '';
        break;

    }

    return interval;
  }

  ngOnDestroy(): void {
    this.stopDataRetrieval();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.statusEventPageActionsQueueService.getStatusEventActionsSubject()
    .pipe(takeUntil(this.destroy$))
    .subscribe((parameters: StatusEventPageActionsParameters) => this.onParametersChange(parameters));

    this.handleDataLoad();
  }

  @HostListener('document:visibilitychange', ['$event'])
  visibilitychange() {
    if (document.hidden) {
      this.isPageVisible = false;
      this.stopDataRetrieval();
      console.log('Page ' + this.pageName + ' is hidden');
    } else {
      this.isPageVisible = true;
      this.handleDataLoad();
      console.log('Page ' + this.pageName + ' is visible');
    }
  }

  public onCurrentReportsSelectedChange(b: boolean) {
    if (this.isCurrentReportsTabSelected === b) {
      return;
    }

    this.isCurrentReportsTabSelected = b;
    this.changeDetector.detectChanges();

    if (b) {
      this.statusEventsCurrent = [];
      this.handleDataLoad();
    } else {
      this.statusEventsPast = [];
      this.stopDataRetrieval();
      this.selectedTimeInterval = TimeInterval.NONE;
      this.isLoadingPastData = true;
      this.sortableHeaders = this.tableHeaderSortableService.initHeaders(this.sortableHeaders, this.sortColumn === 'sortableType' ? 'type' : this.sortColumn, this.sortDirection);
    }
  }

  public onColumnSort({column, direction}: SortEvent) {
    this.sortColumn = column;
    this.sortDirection = direction;
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.statusEventsCurrent = this.tableHeaderSortableService.sortItems<StatusEventPageEventDisplayDetails>(this.statusEventsCurrent, column, direction);
  }

  public onColumnSortPastData({column, direction}: SortEvent) {

    const previousSortColumn = this.sortColumn;
    const previousSortDirection = this.sortDirection;

    this.sortColumn = column;
    this.sortDirection = direction;
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    if (previousSortColumn === column && (previousSortDirection === direction || direction === '')) {
      return;
    }
    this.update();
  }

  public setTimeInterval(event) {
    if (!event?.id) {
      this.selectedTimeInterval = TimeInterval.NONE;
      this.isLoadingPastData = true;
      return;
    }

    this.setShowCustomDatePicker(event);
    this.selectedTimeInterval = event.id;
    this.isLoadingPastData = false;
    this.calculateIntervals(event.id);
  }

  public getStartDate() {
    const now = new Date();
    return String(now.getTime());
  }

  public getStartTime() {
    return {hour: 0, minute: 0, second: 0};
  }

  public onFocusOutStartTime(event) {
    if (!event?.date || event?.time?.hour === undefined || event?.time?.minute === undefined) {
      return;
    }
    this.customStartDate = this.statusEventPageDataService.getDate(event);
  }

  public onFocusOutEndTime(event) {
    if (!event?.date || event?.time?.hour === undefined || event?.time?.minute === undefined) {
      return;
    }
    this.customEndDate = this.statusEventPageDataService.getDate(event);
  }

  public update() {
    this.calculateIntervals(this.selectedTimeInterval);
    this.resetPastReports();
    this.getPastReportsData();
  }

  public onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPageLimit = itemsPerPage;
    this.update();
  }

  public onLoadPageNumber(pageNumber: number): void {
    this.currentPageIndex = pageNumber;
    // load next items
    this.getPastReportsData();
  }

  private formatDate(timestamp: Date): string {
    return this.statusEventPageDataService.formatDateTime(timestamp.getTime(), 'shortDate');
  }

  private formatTime(timestamp: Date): string {
    return this.statusEventPageDataService.formatDateTime(timestamp.getTime(), 'mediumTime');
  }

  private formatDateTime(timestamp: Date): string {
    const date: string = this.formatDate(timestamp);
    const time: string = this.formatTime(timestamp);

    if (!date || !time) {
      return '';
    }
    return date + ' ' + time;

  }

  private formatTimestamp(timestamp: number,): string {
    const date: string = this.statusEventPageDataService.formatDateTime(timestamp, 'shortDate');
    const time: string = this.statusEventPageDataService.formatDateTime(timestamp, 'mediumTime');

    if (!date || !time) {
      return '';
    }
    return date + ' ' + time;
  }

  private calculateIntervals(timeInterval: TimeInterval) {
    const intervals = this.statusEventPageDataService.getIntervals(timeInterval);
    this.endInterval = intervals.endInterval;
    this.startInterval = intervals.startInterval;
  }

  private getStartTimestamp(): number {
    if (this.selectedTimeInterval === TimeInterval.CUSTOM) {
      return this.customStartDate;
    }
    if (!this.startInterval) {
      return new Date().getTime();
    }
    return this.startInterval.getTime();
  }

  private getEndTimestamp(): number {
    if (this.selectedTimeInterval === TimeInterval.CUSTOM) {
      return this.customEndDate;
    }
    if (!this.endInterval) {
      return new Date().getTime();
    }
    return this.endInterval.getTime();
  }


  private getPastReportsData() {
    if (this.selectedTimeInterval === TimeInterval.NONE) {
      this.isLoadingPastData = true;
      return;
    }

    this.isLoadingPastData = true;
    const startInterval = this.getStartTimestamp();
    const endInterval = this.getEndTimestamp();
    const sortDirection: 'ASC' | 'DESC' = this.sortDirection.toLowerCase() === 'asc' ? 'ASC' : 'DESC';
    this.loadingIndicator.show();
    const parameters: StatusEventPagePastDataRequestParameters = new StatusEventPagePastDataRequestParameters(startInterval, endInterval, this.currentPageIndex, this.itemsPerPageLimit, this.actionsParameters.visibleStatusEventSignals, this.actionsParameters.visibleStatusEventTypes, this.sortColumn, sortDirection);
    this.statusEventPageDataService.getPastStatusEventPageData(this.machineId, this.pageId, parameters, this.isTemplatePage)
    .subscribe({
      next: (result: Map<string, StatusEventPageSignalDataResponse[]>) => {
        if (!result || result.size === 0) {
          this.showNoResultsMessage();

        } else {
          this.isLoadingPastData = false;
          this.statusEventsPast = this.filterAndSortEvents(result);
        }
        this.lastPageReached = this.isLastPageReached(result, this.itemsPerPageLimit);
        this.loadingIndicator.hide();
      }, error: (error) => {
        console.log('Error getting past reports', error);
        this.showNoResultsMessage();
        this.loadingIndicator.hide();
        this.isLoadingPastData = true;
      }
    });
  }

  private isLastPageReached(itemMap: Map<string, StatusEventPageSignalDataResponse[]>, itemsPerPage: number): boolean {
    if (!itemMap) {
      return true;
    }

    const keys: string[] = Object.keys(itemMap);
    if (!keys || keys.length === 0) {
      return true;
    }
    let itemsCount: number = 0;
    for (const key of keys) {
      itemsCount += itemMap[key].length;
    }
    return itemsCount < itemsPerPage;


  }

  private resetPastReports() {
    this.resetPaginationPageIndex = true;
    this.currentPageIndex = 0;
    this.isLoadingPastData = true;
    this.lastPageReached = false;
    setTimeout(() => {
      this.resetPaginationPageIndex = false;
    }, 50);

  }

  private showNoResultsMessage() {
    const message = this.translate.instant('There are no past reports for the specified interval');
    this.notification.warning(message, this.COMPONENT_NAME);
  }

  private setShowCustomDatePicker(event): void {
    this.showCustomDatePicker = event.id === TimeInterval.CUSTOM;
  }

  private onParametersChange(parameters: StatusEventPageActionsParameters): void {
    if (parameters.pageId !== this.pageId) {
      return;
    }

    //edit mode changed
    if (this.actionsParameters.isInConfigurationMode != parameters.isInConfigurationMode) {
      this.actionsParameters = cloneDeep(parameters);
      if (parameters.isInConfigurationMode) {
        this.stopDataRetrieval();
      } else {
        this.handleDataLoad();
      }
      return;
    }

    const visibleSignalsIdentical = this.actionsParameters.visibleStatusEventSignals.length === parameters.visibleStatusEventSignals.length &&
        this.actionsParameters.visibleStatusEventSignals.every(function (element) {
          return parameters.visibleStatusEventSignals.includes(element);
        });

    const visibleEventTypesIdentical = this.actionsParameters.visibleStatusEventTypes.length === parameters.visibleStatusEventTypes.length &&
        this.actionsParameters.visibleStatusEventTypes.every(function (element) {
          return parameters.visibleStatusEventTypes.includes(element);
        });

    if (!visibleSignalsIdentical || !visibleEventTypesIdentical) {
      this.actionsParameters = cloneDeep(parameters);
      if (this.isCurrentReportsTabSelected) {
        this.statusEventsCurrent = this.filterAndSortEvents(this.unfilteredEventItemsCurrent);
      } else {
        this.update();
      }
    }

  }

  private handleDataLoad() {
    if (!this.isCurrentReportsTabSelected || !this.isPageVisible || this.actionsParameters.isInConfigurationMode) {
      this.stopDataRetrieval();
      return;
    }
    this.settingsService.isLiveDataStreamingEnabled().subscribe({
      next: value => {
        if (value) {

          this.startDataRetrieval();
        }
        this.liveDataStreamingEnabled = value;
      }
    });
  }

  private stopDataRetrieval() {
    this.loadingIndicator.hide();
    this.statusEventPageDataService.stopDataPolling();
  }

  private startDataRetrieval(): void {
    if (!this.isCurrentReportsTabSelected) {
      return;
    }


    if (this.machineId && this.pageId) {
      console.log('StatusEventPageComponent init');
      this.loadingIndicator.show('Loading');

      this.statusEventPageDataService.startDataPolling(this.machineId, this.pageId, this.isTemplatePage)
      .subscribe({
        next:
            (data: Map<string, StatusEventPageSignalDataResponse[]>) => {
              this.unfilteredEventItemsCurrent = data;
              this.statusEventsCurrent = this.filterAndSortEvents(data);
              this.lastUpdateTimestamp = new Date().getTime();
              this.loadingIndicator.hide();
            }, error: (error) => {
          this.loadingIndicator.hide();
          console.log(error);
        }
      });

    }
  }

  private filterAndSortEvents(data: Map<string, StatusEventPageSignalDataResponse[]>): StatusEventPageEventDisplayDetails[] {
    const filteredEvents = this.statusEventPageDataService
    .parseStatusEventResponseToDisplayDetails(this.statusEventPageConfiguration, data,
        this.actionsParameters.visibleStatusEventTypes, this.actionsParameters.visibleStatusEventSignals);
    return this.tableHeaderSortableService.sortItems<StatusEventPageEventDisplayDetails>(filteredEvents, this.sortColumn, this.sortDirection);
  }

}
